import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { showNotification } from "@mantine/notifications";
import { IconX } from "@tabler/icons-react";

import { isStandardApiError, convertArrayValuesToStrings } from "./apiClient";
import * as Sentry from "@sentry/react";
import { STANDARD_ERROR_MESSAGE } from "../constants";

const DEFAULT_METADATA = {
  page: 1,
  total_pages: 1,
};

export type Params = { [name: string]: any };

export interface useApiProps<DataType> {
  apiFunc?: (params: Params) => Promise<any>;
  defaultData: DataType;
  paginated: boolean;
}

export interface MetaData {
  page: number;
  total_pages: number;
  available_filters?: { [name: string]: any };
  current_filter?: string | null;
}

export interface PaginatedResponse<DataType> {
  responseData: {
    data: DataType;
    metaData: MetaData;
  };
  error: boolean;
  loading: boolean;
  refresh: () => void;
  setParams: Dispatch<SetStateAction<Params>>;
}

export interface NonPaginatedResponse<DataType> {
  responseData: {
    data: DataType;
  };
  error: boolean;
  loading: boolean;
  refresh: () => void;
  setParams: Dispatch<SetStateAction<Params>>;
}

// --- Overload signatures ---
export function useApi<DataType>(props: {
  apiFunc?: (params: Params) => Promise<any>;
  defaultData: DataType;
  paginated: true;
}): PaginatedResponse<DataType>;

export function useApi<DataType>(props: {
  apiFunc?: (params: Params) => Promise<any>;
  defaultData: DataType;
  paginated: false;
}): NonPaginatedResponse<DataType>;

// --- Implementation ---
export function useApi<DataType>({
  apiFunc,
  defaultData,
  paginated,
}: useApiProps<DataType>) {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState(
    paginated ? { page: DEFAULT_METADATA.page } : {}
  );

  const [responseData, setResponseData] = useState<any>(
    paginated
      ? { data: defaultData, metaData: DEFAULT_METADATA }
      : { data: defaultData }
  );

  const request = async () => {
    try {
      if (apiFunc === undefined) return;
      setLoading(true);
      const jsonRes = await apiFunc({ params });
      if (paginated) {
        setResponseData({
          data: jsonRes.data || defaultData,
          metaData: jsonRes.meta_data || DEFAULT_METADATA,
        });
      } else {
        setResponseData({ data: jsonRes || defaultData });
      }
    } catch (e: any) {
      let errorMessage;
      // TODO: Refacotor this logic and document...
      if (isStandardApiError(e)) {
        const tempMessage = convertArrayValuesToStrings(e.data);
        errorMessage =
          typeof tempMessage === "string"
            ? tempMessage
            : Object.values(tempMessage).join("; ");
      } else {
        Sentry.withScope((scope) => {
          scope.setContext("error_obj", e);
          Sentry.captureMessage(
            "useApi received non-standard Error",
            "warning"
          );
        });
        errorMessage = STANDARD_ERROR_MESSAGE;
      }
      setResponseData(
        paginated
          ? { data: defaultData, metaData: DEFAULT_METADATA }
          : { data: defaultData }
      );
      setError(true);
      showNotification({
        message: errorMessage,
        color: "red",
        title: "Error",
        icon: <IconX size={18} />,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    request();
  }, [params]);

  return {
    responseData,
    error,
    loading,
    refresh: request,
    setParams,
  };
}

export default useApi;
